import { AuthTokenManager } from '@classes/AuthTokenManager';
import { getAiApiUrl } from '@utils/aiApiUrl';
import { logError } from '@utils/usefulFuncs';

// Cross-origin transport for the AI API (fapi). Deliberately NOT built on sendApiRequest /
// extendedFetch, which cannot serve this call for two structural reasons:
//   1. sendApiRequest prepends the Rails base URL (`${BASEURL}/api/...`) — fapi is another host.
//   2. extendedFetch computes `skipInterceptor = !sameOrigin` and so drops the Authorization
//      header for any cross-origin URL (src/services/network.js).
//
// Two things here differ from the rest of the service layer on purpose:
//   - `credentials: 'omit'`. fapi's CORS policy is non-credentialed (allow_credentials = False);
//     the repo-wide default of `credentials: 'include'` would fail the browser's CORS check
//     against an explicit-origin, non-credentialed server.
//   - No handle400Statuses. A fapi 403 means "this account lacks the feed_view grant", which is a
//     per-tool gap — routing it into PermissionEffectHandler would bounce the whole app to the
//     in-review page. Session-level loss of auth is still global: AuthTokenManager._doRefresh
//     calls AuthEffectHandler.onLossOfAuth when the refresh itself is rejected.
export const AI_BASEURL = getAiApiUrl();

const GENERIC_ERROR = 'Something went wrong. Please try again.';

const buildRequestInit = (method, body, token, signal) => {
  const headers = new Headers();
  headers.set('Content-Type', 'application/json');
  headers.set('Accept', 'application/json');
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  const init = {
    method: method.toUpperCase(),
    credentials: 'omit',
    headers,
  };
  // Lets a caller abort a long read. Without it a superseded or unwanted request keeps running to
  // completion and its response is merely ignored.
  if (signal) {
    init.signal = signal;
  }
  if (init.method !== 'GET' && body != null) {
    init.body = typeof body === 'string' ? body : JSON.stringify(body);
  }
  return init;
};

// An error response may carry no body, or an HTML/plain-text one — neither should look like a
// transport failure to the caller, which branches on `status`.
const readJson = async (response) => {
  try {
    return await response.json();
  } catch (e) {
    return null;
  }
};

export const buildFapiUrl = (path) => `${AI_BASEURL}${path.startsWith('/') ? path : `/${path}`}`;

// Returns a flat { status, data, error } and never throws, matching the rest of the service layer.
// A 401 buys exactly one refresh-and-retry: a second 401 is reported, not retried, because at that
// point the token is valid and the account simply isn't provisioned for this route.
//
// An aborted request comes back as { aborted: true } with no error: the caller asked for it, so it
// is not a failure and must not be reported to the analyst as one.
export const sendFapiRequest = async (method, path, body, options) => {
  const url = buildFapiUrl(path);
  let response;
  try {
    const token = AuthTokenManager.getAccessToken();
    const signal = options?.signal;
    response = await fetch(url, buildRequestInit(method, body, token, signal));

    if (response.status === 401) {
      let refreshedToken = null;
      try {
        refreshedToken = await AuthTokenManager.refresh(token);
      } catch (e) {
        // A rejected refresh already ran onLossOfAuth; a network error on refresh leaves the
        // session in place. Either way the original 401 is what the caller sees.
        refreshedToken = null;
      }
      if (refreshedToken && refreshedToken !== token) {
        response = await fetch(url, buildRequestInit(method, body, refreshedToken, signal));
      }
    }

    const data = await readJson(response);
    return { status: response.status, data, error: null, aborted: false };
  } catch (e) {
    if (e?.name === 'AbortError' || options?.signal?.aborted) {
      return { status: 0, data: null, error: null, aborted: true };
    }
    logError('sendFapiRequest', e);
    return { status: response?.status ?? 0, data: null, error: GENERIC_ERROR, aborted: false };
  }
};
